import React, { useMemo } from 'react';
import { Badge, IconButton, HStack, Tooltip, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';
import { PencilSimple, Trash } from 'phosphor-react';
import { DataTable } from '../../../../../../components/datatable';
import { IRegimeEspecial } from '../../../../../../types/regimeEspecial';
import { formatCNPJ, formatCurrency, toNumber } from '../../../../../../utils/FormatFunctions';
import { IPagination } from '../../../../../../types/shared';

interface RegimesEspeciaisTableProps {
  data: IRegimeEspecial[];
  pagination: IPagination;
  isFetching?: boolean;
  showEmpresa?: boolean;
  onEdit?: (row: IRegimeEspecial) => void;
  onDelete?: (row: IRegimeEspecial) => void;
}

const esferaColor: Record<string, string> = {
  federal: 'blue',
  estadual: 'purple',
  municipal: 'green',
};

const statusColor: Record<string, string> = {
  ativo: 'green',
  suspenso: 'yellow',
  encerrado: 'red',
};

export function RegimesEspeciaisTable({
  data,
  pagination,
  isFetching,
  showEmpresa = true,
  onEdit,
  onDelete,
}: RegimesEspeciaisTableProps) {
  const columns = useMemo<ColumnDef<IRegimeEspecial>[]>(() => {
    const base: ColumnDef<IRegimeEspecial>[] = [];

    if (showEmpresa) {
      base.push({
        header: 'Empresa',
        accessorKey: 'empresa_nome',
        cell: info => {
          const row = info.row.original;
          return (
            <div className="text-left">
              <Text fontSize="12px" fontWeight="medium" noOfLines={1}>
                {row.empresa_nome ?? `Empresa #${row.empresa_id}`}
              </Text>
              <Text fontSize="10px" color="gray.500">
                {row.empresa_cnpj ? formatCNPJ(row.empresa_cnpj) : ''}
              </Text>
            </div>
          );
        },
      });
    }

    base.push(
      {
        header: 'Esfera',
        accessorKey: 'esfera',
        cell: info => {
          const v = info.getValue() as string;
          return (
            <Badge colorScheme={esferaColor[v] ?? 'gray'} fontSize="9px">
              {v}
            </Badge>
          );
        },
      },
      {
        header: 'Tipo',
        accessorKey: 'tipo',
        cell: info => (info.getValue() as string) || '—',
      },
      {
        header: 'Nº Ato',
        accessorKey: 'numero_ato',
        cell: info => (info.getValue() as string) || '—',
      },
      {
        header: 'Emissor',
        accessorKey: 'emissor',
        cell: info => (info.getValue() as string) || '—',
      },
      {
        header: 'UF',
        accessorKey: 'uf',
        cell: info => (info.getValue() as string) || '—',
      },
      {
        header: 'Vigência',
        accessorKey: 'dt_inicio',
        cell: info => {
          const row = info.row.original;
          const ini = row.dt_inicio ? moment(row.dt_inicio).format('DD/MM/YYYY') : '—';
          const fim = row.dt_fim ? moment(row.dt_fim).format('DD/MM/YYYY') : '—';
          return (
            <Text fontSize="11px">
              {ini} → {fim}
            </Text>
          );
        },
      },
      {
        header: 'Economia',
        accessorKey: 'economia_estimada',
        cell: info => formatCurrency(toNumber(info.getValue() as number | string)),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: info => {
          const v = info.getValue() as string;
          return (
            <Badge colorScheme={statusColor[v] ?? 'gray'} fontSize="9px">
              {v}
            </Badge>
          );
        },
      },
    );

    if (onEdit || onDelete) {
      base.push({
        header: 'Ações',
        id: 'actions',
        cell: info => {
          const row = info.row.original;
          return (
            <HStack justify="center" spacing={1}>
              {onEdit && (
                <Tooltip label="Editar">
                  <IconButton
                    aria-label="Editar"
                    icon={<PencilSimple size={14} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => onEdit(row)}
                  />
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip label="Remover">
                  <IconButton
                    aria-label="Remover"
                    icon={<Trash size={14} />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => onDelete(row)}
                  />
                </Tooltip>
              )}
            </HStack>
          );
        },
      });
    }

    return base;
  }, [showEmpresa, onEdit, onDelete]);

  return (
    <DataTable<IRegimeEspecial>
      columns={columns}
      data={data}
      pagination={pagination}
      isFetching={isFetching}
      emptyMsg="Nenhum regime especial encontrado."
      loadingMsg="Carregando regimes..."
    />
  );
}
